import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest } from '../types';
import { authAPI, updatesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { tokenManager } from '../utils/tokenManager';
import { secureSetItem, secureGetItem, secureRemoveItem } from '../utils/secureStorage';

interface AuthContextType {
  user: User | null;
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await authAPI.login(credentials);

      if (response.user && response.token) {
        setUser(response.user);
        secureSetItem('token', response.token);
        toast.success('Başarıyla giriş yapıldı!');

        // İlk giriş kontrolü - güncelleme sorgusu
        const hasCheckedUpdates = secureGetItem('hasCheckedUpdates');
        if (!hasCheckedUpdates) {
          try {
            await updatesAPI.check();
            secureSetItem('hasCheckedUpdates', 'true');
          } catch (error) {
    
          }
        }

        // Otomatik token yenileme sistemini başlat
        // tokenManager.startAutoRefresh(); // Geçici olarak devre dışı

        return true;
      } else {
        toast.error('Giriş başarısız!');
        return false;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Giriş başarısız!');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Clear cart data on backend before logout
    try {
      const token = secureGetItem('token');
      if (token) {
        // CSRF token'ı al
        const csrfResponse = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/csrf-token`, {
          credentials: 'include'
        });
        const csrfData = await csrfResponse.json();
        
        await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/realtime-cart/clear`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CSRF-Token': csrfData.csrfToken
          },
          credentials: 'include'
        });
        console.log('✅ Cart cleared on logout');
      }
    } catch (error) {
      console.error('❌ Cart clear error on logout:', error);
    }

    authAPI.logout();
    setUser(null);
    secureRemoveItem('hasCheckedUpdates');
    tokenManager.stopAutoRefresh(); // Token yenileme sistemini durdur
    toast.success('Başarıyla çıkış yapıldı');
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = secureGetItem('token');
      if (token) {
        try {
          // Token'ı kontrol et ve gerekirse yenile
          const isValid = await tokenManager.checkAndRefreshToken();
          if (!isValid) {
            secureRemoveItem('token');
            setIsLoading(false);
            return;
          }

          const userData = await authAPI.me();
          setUser(userData);
          
          // Otomatik token yenileme sistemini başlat
          // tokenManager.startAutoRefresh(); // Geçici olarak devre dışı
        } catch (error) {
          secureRemoveItem('token');
          tokenManager.stopAutoRefresh();
        }
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};